<script lang="ts" setup>
import VueTurnstile from 'vue-turnstile'
import debounce from 'lodash/debounce'
import { useRuntimeConfig } from '#app'
import { onMounted, onBeforeUnmount } from 'vue'
import { useAuthStore } from '~/stores/auth'
import { AUTH_CRITERIA } from '~/validators/constant/auth'
import {
  DEBOUNCE_CHECK_FULL_NAME,
  ErrorStatus,
  TIME_OUT_TRACKING,
} from '~/constants'
import { useRegisterForm } from '~/composables/form/use-authentication-form'
import type { SignUpRequestForm } from '~/types/auth'
import { RoutesEnum, DataFormRegister } from '~/enums'
import { useRegisterAddonStore } from '@kira-dancer/nuxt-common/stores'
import { useUserEngagementEvents } from '~/composables/tracking-events/use-user-engagement-events'
import { useUserOnboardingEvents } from '~/composables/tracking-events/use-user-onboarding-events'
import { useModalsStore } from '#lib/stores'

const props = defineProps({
  onSuccess: {
    type: Function,
    required: false,
    default: null,
  },
  customClass: {
    type: String,
    default: '',
  },
})

const { BRAND_NAME, TURNSTILE_SITE_KEY, CAPTCHA_ENABLED } =
  useRuntimeConfig().public
const { onRegister } = useAuth()
const { checkUsernameExists } = useAccount()
const { navigate } = useNavigate()
const authStore = useAuthStore()
const { queries } = useRegisterAddonStore()
const { trackingProfileCompletion } = useUserEngagementEvents()
const { signUpCompleted } = useUserOnboardingEvents()
const { setOpenAuthModal } = useModalsStore()
const timeout = ref()

onMounted(() => {
  authStore.setPreEscape(true)
})

onBeforeUnmount(() => {
  authStore.setPreEscape(false)
  if (timeout.value) {
    clearTimeout(timeout.value)
  }
})

const isLoading = ref(false)
const errorMessage = ref('')
const errorMessageUsername = ref('')
const isInvalidUsername = ref(false)
const tokenCaptcha = ref('')

const onContinueRegister = () => {
  authStore.setWarningEscaped(false)
}

const onCloseWarning = () => {
  authStore.setWarningEscaped(false)
  authStore.setPreEscape(false)
  setOpenAuthModal(false)
  navigate(RoutesEnum.Home)
}

const { values, meta, isSubmitting, handleSubmit, setFieldValue } =
  useRegisterForm()

const disableBtn = computed(
  () =>
    !meta.value.dirty ||
    !meta.value.valid ||
    isLoading.value ||
    isSubmitting.value ||
    isInvalidUsername.value,
)
const loading = computed(() => isLoading.value || isSubmitting.value)

const onCheckUsernameExists = async (value: string): Promise<void> => {
  if (value.length < AUTH_CRITERIA.USER_NAME_MIN_LENGTH) {
    return
  }
  try {
    const data = await checkUsernameExists(value)
    const { exists } = data.value.data
    isInvalidUsername.value = exists

    if (exists) {
      errorMessageUsername.value = 'Tên đăng nhập đã tồn tại'
      return
    }
    errorMessageUsername.value = ''
  } catch {
    errorMessageUsername.value = ''
  }
}

watch(
  () => tokenCaptcha.value,
  (value) => {
    setFieldValue('captchaToken', value)
  },
)

const debouncedChange = debounce((value, name) => {
  if (name === DataFormRegister.Username) {
    onCheckUsernameExists(value.trim())
    return
  }
}, DEBOUNCE_CHECK_FULL_NAME)

const onSubmit = handleSubmit(async (formData: SignUpRequestForm) => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    const request = {
      username: formData.username,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
      fullname: '',
      phoneNumber: formData.phoneNumber,
      captchaToken: formData.captchaToken,
      queries,
    }
    await onRegister(request)
    setOpenAuthModal(false)
    props.onSuccess?.()
    if (timeout.value) {
      clearTimeout(timeout.value)
    }
    timeout.value = setTimeout(() => {
      trackingProfileCompletion()
      signUpCompleted()
    }, TIME_OUT_TRACKING)
  } catch (ex) {
    const status = (ex as { status: ErrorStatus }).status
    switch (status) {
      case ErrorStatus.ExistedUsername:
        errorMessageUsername.value = 'Tên đăng nhập đã tồn tại'
        break
      case ErrorStatus.UserRegisterManyTime:
        errorMessage.value =
          'Bạn đã đăng ký quá nhiều tài khoản. Xin vui lòng thử lại sau'
        break
      default:
        errorMessage.value = (ex as { message: string }).message
    }
  } finally {
    isLoading.value = false
  }
})

const onClearErrorMessage = (filedName: string) => {
  if (filedName === DataFormRegister.Username) {
    errorMessageUsername.value = ''
    return
  }
}

watch(
  () => values,
  () => {
    errorMessage.value = ''
  },
  {
    deep: true,
  },
)
</script>

<template>
  <div>
    <form
      class="form-register"
      :class="{ 'form-register-captcha': CAPTCHA_ENABLED }"
      action="#"
      @submit.prevent="onSubmit"
    >
      <FormInput
        name="username"
        placeholder="Nhập tên đăng nhập của bạn"
        label="Tên đăng nhập"
        :max-length="29"
        :custom-error-message="errorMessageUsername"
        required
        @on-input="debouncedChange(values.username, DataFormRegister.Username)"
        @on-clear-error-message="onClearErrorMessage"
      />
      <FormPassword
        name="password"
        placeholder="Nhập mật khẩu của bạn"
        label="Mật khẩu"
        :max-length="32"
        required
      />
      <FormPassword
        name="confirmPassword"
        placeholder="Nhập mật khẩu của bạn"
        label="Xác nhận lại mật khẩu"
        :max-length="32"
        required
      />
      <FormPhone
        name="phoneNumber"
        placeholder="Nhập số điện thoại của bạn"
        label="Số điện thoại"
        :max-length="10"
        required
      />
      <FormCheckbox name="termAcception">
        <template #description>
          <span
            >Bạn đã đọc và đồng ý với
            <a
              class="text-underline"
              :href="RoutesEnum.TermsAndConditions"
              target="_blank"
              >Điều khoản điều kiện</a
            >
            của chúng tôi.</span
          >
        </template>
      </FormCheckbox>
      <BaseErrorMessage v-if="errorMessage" :message="errorMessage" />
      <BaseButton
        custom-class="form-register__submit-btn"
        type="submit"
        variant="primary"
        button-id="submit-register"
        button-name="submit-register"
        :disabled="disableBtn"
        :loading="loading"
        label="Đăng ký ngay"
      />
      <div v-if="CAPTCHA_ENABLED" class="modal-captcha">
        <VueTurnstile
          v-model="tokenCaptcha"
          :site-key="TURNSTILE_SITE_KEY"
          size="flexible"
          theme="dark"
        />
        <div class="hidden">
          <FormInput name="captchaToken" />
        </div>
      </div>
    </form>
    <div class="banner-login">
      <img src="/images/auth/banner-login-mb.webp" alt="banner mobile" />
    </div>

    <BaseConfirm
      v-model="authStore.warningEscapsed"
      title="Bạn chưa hoàn thành đăng ký"
      :confirm-label="`Tiếp tục`"
      :cancel-label="`Trang chủ`"
      :custom-class="`${props.customClass} modal-confirm-register`"
      image="/images/popup/failed.svg"
      :is-button-grid="true"
      @on-cancel="onCloseWarning"
      @on-confirm="onContinueRegister"
    >
      <template #description>
        <span class="whitespace-nowrap"
          >Còn 1 chút nữa thôi,<br />tiếp tục đăng ký và làm chủ cuộc chơi cùng
          {{ BRAND_NAME }}</span
        >
      </template>
    </BaseConfirm>
  </div>
</template>
<style
  lang="scss"
  src="/assets/styles/components/desktop/form/register-form.scss"
  scoped
></style>
